// The Pro insights view's words: shares, reply times, and the few tips worth acting on, read
// from the numbers `GET /api/seller/insights` derives (`concierge/talent_insights.py` owns
// every number; this module only phrases them). Pure, unit-tested (insights.test.ts). Plain
// hyphens only: a long dash in anything a talent reads fails the repo-wide copy gate.

import type { SellerInsights, SellerRateCompare } from "@recruiter/shared";

import type { StageTone } from "./talent-stage";

/** A 0..1 share as a whole percent: 0.456 -> "46%". "-" without a base. */
export function pct(share: number | null | undefined): string {
  return share === null || share === undefined ? "-" : `${Math.round(share * 100)}%`;
}

/** Minutes to a first reply as a short label: "under a minute", "45m", "2h 15m", "1d 3h".
 *  "-" when nobody replied yet. */
export function replyTime(minutes: number | null | undefined): string {
  if (minutes === null || minutes === undefined || minutes < 0) return "-";
  if (minutes < 1) return "under a minute";
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return minutes % 60 ? `${hours}h ${minutes % 60}m` : `${hours}h`;
  const days = Math.floor(hours / 24);
  return hours % 24 ? `${days}d ${hours % 24}h` : `${days}d`;
}

/** Where the talent sits against the field on a rate where higher is better. A gap under
 *  five points reads as even: these are small samples. */
export function standing(rate: SellerRateCompare): "ahead" | "behind" | "even" | null {
  if (rate.you === null || rate.field === null) return null;
  const gap = rate.you - rate.field;
  if (Math.abs(gap) < 0.05) return "even";
  return gap > 0 ? "ahead" : "behind";
}

export interface InsightTip {
  tone: Extract<StageTone, "ok" | "wait" | "ai">;
  title: string;
  body: string;
}

/** At most this many tips: a list of ten is a list nobody reads. */
export const MAX_TIPS = 3;

/** What Mira would tell the talent first, most useful first. Each rule fires only on a
 *  real gap, so a talent doing fine hears what is working instead. */
export function insightTips(i: SellerInsights): InsightTip[] {
  const tips: InsightTip[] = [];
  if (!i.minimums_set) {
    tips.push({
      tone: "ai",
      title: "Set your minimum rates",
      body: "Mira checks every new project against them, so you only hear about work that pays enough.",
    });
  }
  const you = i.reply_minutes;
  const field = i.field_reply_minutes;
  if (you !== null && field !== null && you > field * 1.5 && you - field >= 30) {
    tips.push({
      tone: "wait",
      title: "Reply sooner",
      body: `You take ${replyTime(you)} to reply. Talents on the same projects take ${replyTime(field)}. The first replies are the ones the client reads first.`,
    });
  } else if (you !== null && field !== null && you <= field && i.first_to_reply > 0) {
    tips.push({
      tone: "ok",
      title: "Your speed is an edge",
      body: `You were the first talent to reply on ${i.first_to_reply} of ${i.replied} projects.`,
    });
  }
  if (standing(i.proposal_rate) === "behind") {
    tips.push({
      tone: "wait",
      title: "Send more proposals",
      body: `You quote on ${pct(i.proposal_rate.you)} of projects, the field on ${pct(i.proposal_rate.field)}. A quick price, even a range, keeps you in the running.`,
    });
  }
  const vsField = i.quote_vs_field_pct;
  if (vsField !== null && vsField >= 20 && standing(i.win_rate) !== "ahead") {
    tips.push({
      tone: "wait",
      title: "Your price may be costing you",
      body: `Your quotes sit ${vsField}% above the typical quote on the same projects.`,
    });
  } else if (vsField !== null && vsField <= -20) {
    tips.push({
      tone: "ai",
      title: "You may be under-pricing",
      body: `Your quotes sit ${-vsField}% below the typical quote on the same projects. There may be room to charge more.`,
    });
  }
  const vsBudget = i.quote_vs_budget_pct;
  if (vsBudget !== null && vsBudget >= 25) {
    tips.push({
      tone: "wait",
      title: "Quotes over the budget",
      body: `Your quotes run ${vsBudget}% over the client's budget. Show what the extra buys, or offer a smaller first step.`,
    });
  }
  if (standing(i.win_rate) === "ahead") {
    tips.push({
      tone: "ok",
      title: "You win more than most",
      body: `You win ${pct(i.win_rate.you)} of your proposals, the field ${pct(i.win_rate.field)}.`,
    });
  }
  return tips.slice(0, MAX_TIPS);
}
